import React from 'react';
import './artemis-loading.css';

// Self-contained on purpose — see the artemis-loading.css header. Deliberately
// NOT className="artemis-root": this screen renders before the lazy artemis
// chunk (and artemis.css) loads and must look identical before and after.

interface ArtemisLoadingScreenProps {
  /** Phase line, e.g. "Checking access…". Defaults to a generic label. */
  status?: string;
  /** Boot gate index (1-4). Renders the phase-dot trail; omitted → no dots. */
  phase?: 1 | 2 | 3 | 4;
}

// The staggered entrance should play once per tab boot, not once per gate —
// the loader unmounts/remounts as each boot gate hands off to the next.
let hasBootedThisTab = false;

const BOOT_PHASES = [1, 2, 3, 4] as const;

export const ArtemisLoadingScreen: React.FC<ArtemisLoadingScreenProps> = ({
  status = 'Loading Artemis…',
  phase
}) => {
  const [animateIn] = React.useState(() => !hasBootedThisTab);
  React.useEffect(() => {
    hasBootedThisTab = true;
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`artemis-loading-screen${animateIn ? ' artemis-loading-enter' : ''}`}
    >
      <div className="artemis-loading-content">
        {/* Hunt reticle + arrow emblem — pure geometry, no image asset */}
        <svg
          className="artemis-loading-emblem"
          viewBox="0 0 220 220"
          fill="none"
          aria-hidden="true"
        >
          <circle className="al-ring-dashed" cx="110" cy="110" r="103" strokeWidth="1.25" strokeDasharray="2 6" />
          <g className="al-tick" strokeWidth="1.5" strokeLinecap="round">
            <path d="M110 7v12" />
            <path d="M110 201v12" />
            <path d="M7 110h12" />
            <path d="M201 110h12" />
          </g>
          <circle className="al-ring-hair" cx="110" cy="110" r="82" strokeWidth="1" />
          <circle className="al-ring-core" cx="110" cy="110" r="63" strokeWidth="1.5" />
          <g className="al-cross" strokeWidth="1" strokeLinecap="round">
            <path d="M110 47v12" />
            <path d="M110 161v12" />
            <path d="M47 110h12" />
            <path d="M161 110h12" />
          </g>
          <circle className="al-ping" cx="110" cy="110" r="63" strokeWidth="1.25" />
          <circle className="al-ping al-ping--late" cx="110" cy="110" r="63" strokeWidth="1.25" />
          {/* Arrow pierces the core ring: tips at r≈72 on the 45° diagonal */}
          <path
            className="al-arrow al-arrow-shaft"
            pathLength={1}
            d="M59.1 160.9 L160.9 59.1"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <g
            className="al-arrow al-arrow-detail"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path className="al-arrow-head" strokeWidth="2.5" d="M144.9 64.9 L160.9 59.1 L155.1 75.1" />
            <path d="M64.8 155.2 L52.2 158.6" />
            <path d="M72.5 147.5 L59.9 150.9" />
            <path d="M64.8 155.2 L61.4 167.8" />
            <path d="M72.5 147.5 L69.1 160.1" />
          </g>
        </svg>

        <div className="artemis-loading-wordblock">
          <div className="artemis-loading-word">ARTEMIS</div>
          <div className="artemis-loading-kicker">Lendwise Mortgage, Inc.</div>
        </div>

        <div className="artemis-loading-progress">
          <div className="artemis-loading-track" aria-hidden="true" />
          {phase !== undefined && (
            <div className="artemis-loading-dots" aria-hidden="true">
              {BOOT_PHASES.map((step) => (
                <span
                  key={step}
                  className="artemis-loading-dot"
                  data-state={step < phase ? 'done' : step === phase ? 'current' : 'next'}
                />
              ))}
            </div>
          )}
          <div className="artemis-loading-status">
            {status}
            <span className="artemis-loading-cursor" aria-hidden="true">
              ▍
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtemisLoadingScreen;
